import { useEffect, useId, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import { CalendarDaysIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { fromDateOnlyString, toDateOnlyString } from "../../shared/dateOnly";
import { buttonClass, secondaryClass } from "./FormControls";
import FormFilterSelect from "./FormFilterSelect";
import { advancedKeys, type AdvancedFormFilters, type FormFilterProps } from "./formFilterTypes";

function SubmissionDateFilter({
  draft,
  update,
  error,
}: {
  draft: AdvancedFormFilters;
  update: (key: keyof AdvancedFormFilters, value: string) => void;
  error: string;
}) {
  const [dateField, setDateField] = useState<"date_from" | "date_to" | null>(null);
  const id = useId();
  const calendarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (dateField) calendarRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [dateField]);
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium text-gray-700">Submission date</legend>
      <div className="grid grid-cols-2 gap-3">
        {(["date_from", "date_to"] as const).map((key) => (
          <div key={key} className="min-w-0">
            <span id={`${id}-${key}-label`} className="mb-1 block text-xs text-gray-500">
              {key === "date_from" ? "From" : "To"}
            </span>
            <button
              id={`${id}-${key}`}
              type="button"
              aria-labelledby={`${id}-${key}-label ${id}-${key}`}
              aria-expanded={dateField === key}
              aria-controls={dateField === key ? `${id}-calendar` : undefined}
              onClick={() => setDateField(dateField === key ? null : key)}
              className="flex min-h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:border-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
            >
              <span className="truncate">
                {fromDateOnlyString(draft[key])?.toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                }) || "Any date"}
              </span>
              <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
            </button>
          </div>
        ))}
      </div>
      {dateField && (
        <div
          ref={calendarRef}
          id={`${id}-calendar`}
          className="mt-3"
          role="group"
          aria-label={dateField === "date_from" ? "Choose start date" : "Choose end date"}
        >
          <Calendar
            inline
            value={fromDateOnlyString(draft[dateField])}
            onChange={(event) => {
              update(dateField, toDateOnlyString(event.value as Date | null) || "");
              document.getElementById(`${id}-${dateField}`)?.focus();
              setDateField(null);
            }}
            showButtonBar
            className="w-full"
            pt={{
              panel: { style: { width: "100%" } },
              day: { style: { padding: "0.125rem" } },
              dayLabel: { style: { width: "2rem", height: "2rem" } },
            }}
          />
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </fieldset>
  );
}

export default function AdvancedFilters({
  params,
  options,
  customerId,
  onApply,
  close,
}: Pick<FormFilterProps, "params" | "options" | "customerId" | "onApply"> & { close: () => void }) {
  const [draft, setDraft] = useState<AdvancedFormFilters>(
    () =>
      Object.fromEntries(
        advancedKeys.map((key) => [key, params.get(key) || ""])
      ) as AdvancedFormFilters
  );
  const [error, setError] = useState("");
  const update = (key: keyof AdvancedFormFilters, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setError("");
  };
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (draft.date_from && draft.date_to && draft.date_from > draft.date_to) {
          setError("Choose an end date on or after the start date.");
          return;
        }
        onApply(draft);
        close();
      }}
      className="space-y-4 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-900">Filter forms</h2>
          <p className="mt-1 text-sm text-gray-500">
            Narrow requests by people and submission date.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close filters"
          onClick={close}
          className="rounded-md p-1 text-gray-500 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        >
          <XMarkIcon aria-hidden="true" className="h-5 w-5" />
        </button>
      </div>
      {!customerId && (
        <FormFilterSelect
          label="Customer"
          value={draft.customer_id}
          options={[{ value: "", label: "All customers" }, ...(options?.customers || [])]}
          onChange={(value) => update("customer_id", value)}
          disabled={!options}
        />
      )}
      <FormFilterSelect
        label="Submitted by"
        value={draft.created_by}
        options={[{ value: "", label: "Everyone" }, ...(options?.submitters || [])]}
        onChange={(value) => update("created_by", value)}
        disabled={!options}
      />
      <SubmissionDateFilter draft={draft} update={update} error={error} />
      <div className="sticky bottom-0 -mx-4 -mb-4 flex justify-end gap-2 border-t border-gray-100 bg-white p-4">
        <button type="button" className={secondaryClass} onClick={close}>
          Cancel
        </button>
        <button type="submit" className={buttonClass}>
          Apply filters
        </button>
      </div>
    </form>
  );
}
